import React, { useState, useCallback } from "react"
import {
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Col,
  Label,
  Input,
  FormFeedback,
  Button,
  Form,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import { toast } from "react-toastify"
import { useEditShowroom } from "hooks/showroomHook"
import ButtonTooltip from "components/Common/ButtonTooltip"

export default function ShowroomDetailsUpdate({ showroom = null }) {
  const [modal, setModal] = useState(false)
  const toggle = () => setModal(!modal)

  const { mutateAsync: editShowroom, isPending: showroomEditLoading } =
    useEditShowroom()

  // ✅ Validation schema
  const validationSchema = Yup.object({
    capacity: Yup.number().nullable().min(0, "Must be positive"),
    has_service_center: Yup.boolean(),
    has_parking: Yup.boolean(),
    opening_time: Yup.string().nullable(),
    closing_time: Yup.string().nullable(),
    latitude: Yup.number().nullable(),
    longitude: Yup.number().nullable(),
    description: Yup.string().nullable(),
    is_active: Yup.boolean(),
  })

  // ✅ Submit handler
  const onSubmit = useCallback(
    async values => {
      try {
        const res = await editShowroom({ id: showroom?.id, ...values })
        toggle()
        toast.success(res?.message ?? "Details updated successfully.")
      } catch (error) {
        console.error("Failed to update details:", error)
        toast.error("Failed to update details.")
      }
    },
    [editShowroom, showroom]
  )

  // ✅ Formik setup
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      capacity: showroom?.capacity || "",
      has_service_center: showroom?.has_service_center || false,
      has_parking: showroom?.has_parking ?? true,
      opening_time: showroom?.opening_time || "",
      closing_time: showroom?.closing_time || "",
      latitude: showroom?.latitude || "",
      longitude: showroom?.longitude || "",
      description: showroom?.description || "",
      is_active: showroom?.is_active ?? true,
    },
    validationSchema,
    onSubmit,
  })

  return (
    <>
      {/* ✅ Edit button */}
      <div className="d-flex justify-content-end">
        <ButtonTooltip
          type="edit"
          onClick={toggle}
          btnId="edit-showroom-details"
          btnName="Edit Showroom Details"
        />
      </div>

      {/* ✅ Modal */}
      <Modal isOpen={modal} toggle={toggle} centered size="lg">
        <ModalHeader toggle={toggle}>Edit Showroom Details</ModalHeader>
        <ModalBody>
          <Form
            onSubmit={e => {
              e.preventDefault()
              validation.handleSubmit()
            }}
          >
            <Row>
              <Col md={6}>
                <Label>Capacity</Label>
                <Input
                  type="number"
                  name="capacity"
                  value={validation.values.capacity}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.capacity}
                />
                <FormFeedback>{validation.errors.capacity}</FormFeedback>
              </Col>

              <Col md={6}>
                <Label>Status</Label>
                <Input
                  type="select"
                  name="is_active"
                  value={validation.values.is_active}
                  onChange={e =>
                    validation.setFieldValue(
                      "is_active",
                      e.target.value === "true"
                    )
                  }
                >
                  <option value={true}>Active</option>
                  <option value={false}>Inactive</option>
                </Input>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={6}>
                <Label>Service Center</Label>
                <Input
                  type="select"
                  name="has_service_center"
                  value={validation.values.has_service_center}
                  onChange={e =>
                    validation.setFieldValue(
                      "has_service_center",
                      e.target.value === "true"
                    )
                  }
                >
                  <option value={true}>Yes</option>
                  <option value={false}>No</option>
                </Input>
              </Col>

              <Col md={6}>
                <Label>Parking Available</Label>
                <Input
                  type="select"
                  name="has_parking"
                  value={validation.values.has_parking}
                  onChange={e =>
                    validation.setFieldValue(
                      "has_parking",
                      e.target.value === "true"
                    )
                  }
                >
                  <option value={true}>Yes</option>
                  <option value={false}>No</option>
                </Input>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={6}>
                <Label>Opening Time</Label>
                <Input
                  type="time"
                  name="opening_time"
                  value={validation.values.opening_time}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.opening_time}
                />
              </Col>

              <Col md={6}>
                <Label>Closing Time</Label>
                <Input
                  type="time"
                  name="closing_time"
                  value={validation.values.closing_time}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.closing_time}
                />
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={6}>
                <Label>Latitude</Label>
                <Input
                  type="number"
                  step="0.0000001"
                  name="latitude"
                  value={validation.values.latitude}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.latitude}
                />
              </Col>

              <Col md={6}>
                <Label>Longitude</Label>
                <Input
                  type="number"
                  step="0.0000001"
                  name="longitude"
                  value={validation.values.longitude}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.longitude}
                />
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={12}>
                <Label>Description</Label>
                <Input
                  type="textarea"
                  rows={3}
                  name="description"
                  value={validation.values.description}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.description}
                />
                <FormFeedback>{validation.errors.description}</FormFeedback>
              </Col>
            </Row>

            <div className="text-end mt-4">
              <Button
                type="submit"
                color="success"
                disabled={showroomEditLoading}
              >
                Save Changes
                {showroomEditLoading && "..."}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </>
  )
}
